import * as React from "react";
import { useNavigate } from "react-router";

import { ulid } from "../../util/ulid";
import useDialog from "../Dialog/useDialog";
import { useSelectedSport } from "../common/context/sport";
import { useCurrentUser } from "../hooks/currentUser";

import AskVolleyOverlay from "./AskVolleyOverlay";
import { ASK_PAGE_SURFACE, recordPromptEvent } from "./api";
import { clearChatSession, loadChatSession } from "./chatSession";
import { statsRouteForSport } from "./constants";
import type { ThreadMessage } from "./types";
import { useAssessmentLaunch } from "./useAssessmentLaunch";
import { useDuprConnect } from "./useDuprConnect";

/**
 * Ask Volley as a place, not as an answer.
 *
 * The overlay has only ever been reachable by answering a home-screen prompt,
 * which meant a support question could be asked exactly once per session and
 * only if the widget happened to be showing. This route is the same chat with
 * nothing prompting it: no prompt, no options, no first-run spotlight — just
 * the composer.
 *
 * It owns its own impression, because every event still needs one to join on,
 * and it is what stitches a session's turns together in reporting. The prompt
 * dimension is simply absent, which is why `home_prompt_events` now permits it
 * to be; `surface` is what tells these asks from the widget's.
 */
export default function AskVolleyPage(): React.JSX.Element {
    const navigate = useNavigate();
    const { currentUser } = useCurrentUser();
    const { setDialogType } = useDialog();
    const { selected: sport, getIdFromSport } = useSelectedSport();
    const userId = currentUser?.id;
    const { launchAssessment, dialog: assessmentDialog } =
        useAssessmentLaunch();
    const { launchDuprConnect, dialog: duprDialog } = useDuprConnect();

    const sportId = React.useMemo(() => {
        try {
            return getIdFromSport(sport);
        } catch {
            return undefined;
        }
    }, [getIdFromSport, sport]);

    // A chat left to open a workout or an article, if it was left from here.
    // Read once on mount and consumed, exactly as the widget does: re-reading
    // it on every render would re-restore a chat the user has moved past.
    const [restored] = React.useState(() => {
        const snapshot = loadChatSession(userId);
        if (snapshot?.surface !== ASK_PAGE_SURFACE) return undefined;
        clearChatSession(userId);
        return snapshot;
    });

    // One impression per visit, reused by the restored chat so a round trip
    // through a workout stays one session rather than reading as two.
    const [impressionUlid] = React.useState(
        () => restored?.impressionUlid ?? ulid(),
    );

    // A restored chat is a continuation, not a new arrival: recording a second
    // impression for it would count one visit twice.
    const impressionRecorded = React.useRef(Boolean(restored));
    React.useEffect(() => {
        if (impressionRecorded.current) return;
        impressionRecorded.current = true;
        recordPromptEvent({
            eventType: "impression",
            impressionUlid,
            sportId,
            surface: ASK_PAGE_SURFACE,
            metadata: { action: "open" },
        });
    }, [impressionUlid, sportId]);

    // Back to wherever they came from. `navigate(-1)` rather than a fixed home
    // route: the launcher is reachable from every screen, and dumping someone
    // on the home screen because they asked a question would lose their place.
    const handleClose = React.useCallback(() => {
        recordPromptEvent({
            eventType: "interaction",
            impressionUlid,
            sportId,
            surface: ASK_PAGE_SURFACE,
            metadata: { action: "overlay_close" },
        });
        navigate(-1);
    }, [impressionUlid, navigate, sportId]);

    return (
        <>
            <AskVolleyOverlay
                impressionUlid={impressionUlid}
                surface={ASK_PAGE_SURFACE}
                sportName={sport}
                sportId={sportId}
                restored={
                    restored
                        ? {
                              thread: restored.thread as ThreadMessage[],
                              conversationUlid: restored.conversationUlid,
                          }
                        : undefined
                }
                onClose={handleClose}
                // These four are the prompt's own escape hatches — an option
                // that launches the assessment, links a rating, or opens the
                // dashboard instead of answering. Nothing here can reach them
                // (there are no options without a prompt), but the overlay
                // takes them as required props and wiring them to the real
                // flows is more honest than passing four no-ops that would
                // silently do nothing if an option ever did arrive.
                onLaunchAssessment={(option) => {
                    void launchAssessment(option, sport);
                }}
                onConnectDupr={() => launchDuprConnect()}
                onConnectApta={() => setDialogType("APTAId")}
                onSeeStats={() => navigate(statsRouteForSport(sport))}
            />
            {assessmentDialog}
            {duprDialog}
        </>
    );
}
